"use client";

import {
  getPerticularCMSPageSectionsContent,
  updatePerticularCMSPageSections,
} from "@/actions/apiClientActions/cms";
import { globalStyleObj } from "@/app/assets/styles";
import {
  AboutPageCMSForm,
  AboutUsFirstTabCMSFormFields,
  AboutUsFourthTabCMSFormFields,
  AboutUsSecondTabCMSFormFields,
  AboutUsThirdTabCMSFormFields,
  AmlCTFRulesRegulationsInputFilds,
  CareersCMSForm,
  ContactUsCMSForm,
  CoursesCMSForm,
  FAQsCMSForm,
  FujairahPartnersCMSForm,
  HomePageCMSForm,
  InvestorsCornerCommonFormFields,
  InvestorsCornerFirstTabFormFields,
  InvestorsCornerSecondTabFormFields,
  InvestorsCornerThirdTabFormFields,
  LanguageTabs,
  MediaCenterFirstTabCMSFormFields,
  MediaCenterFourthTabCMSFormFields,
  MediaCenterPageCMSForm,
  MediaCenterSecondTabCMSFormFields,
  MediaCenterThirdTabCMSFormFields,
  NewsAndBlogsCMSForm,
  NormalBrowseFileInputFiled,
  NormalTextEditorInputFiled,
  NormalTextInputField,
  OffersCMSForm,
  OurServicesPageCMSForm,
  PrivacyAndQualityPolicyCMSForm,
  RegistrationProcessCMSForm,
  StartYourBusinessPageCMSForm,
} from "@/components";
import { Skeleton } from "@/components/ui/skeleton";
import { useErrorToast, useSuccessToast } from "@/lib/hooks";
import { useAppSelector } from "@/store/hooks";
import { getCustomTheme } from "@/utils/colors";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { ClipLoader } from "react-spinners";
import InvestorsCornerFourthTabFormFields from "./investors-cormer/InvestorsCornerFourthTabFormFields";

const MainSectionForm = ({
  userId,
  linkId,
  languages,
  allFilesResponse,
  searchValue,
  selectedFileType,
  adminRole,
  permissionsList,
}) => {
  // States
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState("en");
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetching search parameters from the URL
  const searchParams = useSearchParams();
  const selectedTab = searchParams.get("tab");

  // Filtering active languages from the provided language list
  const activeLanguages = languages.filter((lang) => lang.status);

  // Extracting theme-related properties from the Redux store
  const { layoutThemePrimaryColorType } = useAppSelector(
    (state) => state.layout
  );
  const { bgColor, hoverBgColor, textColor, hexCode } = useMemo(
    () => getCustomTheme({ layoutThemePrimaryColorType }),
    [layoutThemePrimaryColorType]
  );

  // Function to fetch CMS content based on the active language tab
  const getCMSContent = async () => {
    try {
      setIsFetching(true);

      const response = await getPerticularCMSPageSectionsContent(
        userId,
        linkId,
        activeTab
      );

      const data =
        response?.success && response?.contentDetails
          ? response.contentDetails
          : {};

      setFormData(data);
    } catch (error) {
      console.log("Failed to fetch CMS page sections content:", error);
      setFormData({});
    } finally {
      setIsFetching(false);
    }
  };

  // Effect to fetch content whenever the active language tab changes
  useEffect(() => {
    if (!isSubmitting) {
      getCMSContent();
    }
  }, [activeTab, isSubmitting]);

  // Handle Iamge Change
  const handleImageChange = (keyName, value) => {
    setFormData({ ...formData, [keyName]: value });
  };

  // Handle Text Input Change
  const handleTextInputChange = (e) => {
    const keyName = e.target.name;
    const value = e.target.value;

    setFormData({ ...formData, [keyName]: value });
  };

  // Handle Text Editor Change
  const handleTextEditorInputChange = (content, keyName) => {
    setFormData({ ...formData, [keyName]: content });
  };

  // Handle Add Only Value Repeater
  const addOnlyValueRepeaterChange = (keyName) => {
    const field = formData?.[keyName] || [];
    const newField = [...field, ""];

    setFormData({ ...formData, [keyName]: newField });
  };

  // Handle Remove Only Value Repeater
  const removeOnlyValueRepeaterChange = (keyName, valueIndex) => {
    const field = formData[keyName];
    const updatedField =
      valueIndex >= 0
        ? field.filter((item, index) => index !== valueIndex)
        : field;

    setFormData({ ...formData, [keyName]: updatedField });
  };

  // Handle Update Only Value Repeater
  const updateOnlyValueRepeaterChange = (keyName, index, value) => {
    const field = formData[keyName];
    const updatedField =
      index >= 0 ? field.map((item, i) => (i === index ? value : item)) : field;

    setFormData({ ...formData, [keyName]: updatedField });
  };

  // Handle Add Multi Value Repeater
  const addMultiValueRepeaterChange = (keyName, structure = {}) => {
    setFormData((prev) => {
      const field = Array.isArray(prev[keyName]) ? prev[keyName] : [];
      return {
        ...prev,
        [keyName]: [...field, structure],
      };
    });
  };

  // Handle Remove Multi Value Repeater
  const removeMultiValueRepeaterChange = (keyName, index) => {
    setFormData((prev) => {
      const field = Array.isArray(prev[keyName]) ? prev[keyName] : [];

      if (index < 0 || index >= field.length) return prev;

      const updatedField = field.filter((_, i) => i !== index);

      return {
        ...prev,
        [keyName]: updatedField,
      };
    });
  };

  // Handle Update Multi Value Repeater
  const updateMultiValueRepeaterChange = (keyName, index, valueKey, value) => {
    setFormData((prev) => {
      const field = Array.isArray(prev[keyName]) ? prev[keyName] : [];

      const updatedField =
        index >= 0
          ? field.map((item, i) =>
              i === index ? { ...item, [valueKey]: value } : item
            )
          : field;

      return {
        ...prev,
        [keyName]: updatedField,
      };
    });
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    if (Object.keys(formData).length === 0) {
      useErrorToast("Please fill fields before submitting.");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await updatePerticularCMSPageSections(
        userId,
        linkId,
        formData,
        activeTab
      );

      if (response.success) {
        useSuccessToast(
          response?.message || "CMS Page sections updated successfully."
        );
      } else {
        useErrorToast(response?.message || "Something went wrong.");
      }
    } catch (error) {
      console.log("Failed to update CMS page sections:", error);
      useErrorToast("Failed to update CMS page sections.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`${globalStyleObj.backgroundLight900Dark300} w-full rounded-sm shadow-light`}
    >
      {activeLanguages.length > 1 && (
        <LanguageTabs
          languages={activeLanguages}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
        />
      )}

      <form onSubmit={handleSubmit} className={`p-3 sm:p-5`}>
        {linkId === "home" && (
          <HomePageCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            // Text Input Field Change
            handleTextInputChange={handleTextInputChange}
            // Text Editor Field Change
            handleTextEditorInputChange={handleTextEditorInputChange}
            // Image Change
            handleImageChange={handleImageChange}
            // Repeater Changes
            addOnlyValueRepeaterChange={addOnlyValueRepeaterChange}
            removeOnlyValueRepeaterChange={removeOnlyValueRepeaterChange}
            updateOnlyValueRepeaterChange={updateOnlyValueRepeaterChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
            colorGrade={{
              bgColor,
              hoverBgColor,
              textColor,
              hexCode,
            }}
          />
        )}

        {linkId === "about-us" && (
          <AboutPageCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            // Normal Field Change
            handleTextInputChange={handleTextInputChange}
            handleTextEditorInputChange={handleTextEditorInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "about-fujairah" && (
          <AboutUsFirstTabCMSFormFields
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleTextEditorInputChange={handleTextEditorInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "about-fujairah-freezone" && (
          <AboutUsSecondTabCMSFormFields
            activeTab={activeTab}
            selectedTab={selectedTab}
            isFetching={isFetching}
            formData={formData}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleImageChange={handleImageChange}
            addOnlyValueRepeaterChange={addOnlyValueRepeaterChange}
            removeOnlyValueRepeaterChange={removeOnlyValueRepeaterChange}
            updateOnlyValueRepeaterChange={updateOnlyValueRepeaterChange}
            addMultiValueRepeaterChange={addMultiValueRepeaterChange}
            removeMultiValueRepeaterChange={removeMultiValueRepeaterChange}
            updateMultiValueRepeaterChange={updateMultiValueRepeaterChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
            colorGrade={{
              bgColor,
              hoverBgColor,
              textColor,
              hexCode,
            }}
          />
        )}

        {linkId === "mission-statement" && (
          <AboutUsThirdTabCMSFormFields
            activeTab={activeTab}
            selectedTab={selectedTab}
            isFetching={isFetching}
            formData={formData}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleTextEditorInputChange={handleTextEditorInputChange}
            onChangeImage={handleImageChange}
            addMultiValueRepeaterChange={addMultiValueRepeaterChange}
            removeMultiValueRepeaterChange={removeMultiValueRepeaterChange}
            updateMultiValueRepeaterChange={updateMultiValueRepeaterChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
            colorGrade={{
              bgColor,
              hoverBgColor,
              textColor,
              hexCode,
            }}
          />
        )}

        {linkId === "messages" && (
          <AboutUsFourthTabCMSFormFields
            activeTab={activeTab}
            selectedTab={selectedTab}
            isFetching={isFetching}
            formData={formData}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            addMultiValueRepeaterChange={addMultiValueRepeaterChange}
            removeMultiValueRepeaterChange={removeMultiValueRepeaterChange}
            updateMultiValueRepeaterChange={updateMultiValueRepeaterChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
            colorGrade={{
              bgColor,
              hoverBgColor,
              textColor,
              hexCode,
            }}
          />
        )}

        {linkId === "organization-structure" &&
          selectedTab === "section-1" &&
          activeTab === "en" && (
            <NormalBrowseFileInputFiled
              isFetching={isFetching}
              labelText="Structure Image"
              fieldId="organization-structure-image"
              allFilesResponse={allFilesResponse}
              searchValue={searchValue}
              selectedFileType={selectedFileType}
              onChangeImageFunction={(id) =>
                id
                  ? handleImageChange("organization-structure-image", id)
                  : handleImageChange("organization-structure-image", "")
              }
              selectedFileId={formData?.["organization-structure-image"] || ""}
              adminRole={adminRole}
              permissionsList={permissionsList}
              // extraContainerClasses="mb-5"
            />
          )}

        {linkId === "start-your-business" && (
          <StartYourBusinessPageCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleTextEditorInputChange={handleTextEditorInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "our-services" && (
          <OurServicesPageCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleImageChange={handleImageChange}
            // Repeater Changes
            addOnlyValueRepeaterChange={addOnlyValueRepeaterChange}
            removeOnlyValueRepeaterChange={removeOnlyValueRepeaterChange}
            updateOnlyValueRepeaterChange={updateOnlyValueRepeaterChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
            colorGrade={{
              bgColor,
              hoverBgColor,
              textColor,
              hexCode,
            }}
          />
        )}

        {linkId === "media-center" && (
          <MediaCenterPageCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            // Normal Field Change
            handleTextInputChange={handleTextInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "news-and-events" && (
          <MediaCenterFirstTabCMSFormFields
            selectedTab={selectedTab}
            formData={formData}
            isFetching={isFetching}
            handleTextInputChange={handleTextInputChange}
          />
        )}

        {linkId === "photo-gallery" && (
          <MediaCenterSecondTabCMSFormFields
            selectedTab={selectedTab}
            formData={formData}
            isFetching={isFetching}
            handleTextInputChange={handleTextInputChange}
          />
        )}

        {linkId === "video-gallery" && (
          <MediaCenterThirdTabCMSFormFields
            selectedTab={selectedTab}
            formData={formData}
            isFetching={isFetching}
            handleTextInputChange={handleTextInputChange}
          />
        )}

        {linkId === "commercial-movie" && (
          <MediaCenterFourthTabCMSFormFields
            selectedTab={selectedTab}
            formData={formData}
            isFetching={isFetching}
            handleTextInputChange={handleTextInputChange}
          />
        )}

        {linkId === "investors-corner" && (
          <InvestorsCornerCommonFormFields
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            // Normal Field Change
            handleTextInputChange={handleTextInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "business-club" && (
          <InvestorsCornerFirstTabFormFields
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            // Normal Field Change
            handleTextInputChange={handleTextInputChange}
            handleTextEditorInputChange={handleTextEditorInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "advertise-with-us" && (
          <InvestorsCornerSecondTabFormFields
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            // Normal Field Change
            handleTextInputChange={handleTextInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "listed-companies-or-investors" && (
          <InvestorsCornerThirdTabFormFields
            selectedTab={selectedTab}
            formData={formData}
            isFetching={isFetching}
            handleTextInputChange={handleTextInputChange}
          />
        )}

        {linkId === "downloads" && (
          <InvestorsCornerFourthTabFormFields
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            // Normal Field Change
            handleTextInputChange={handleTextInputChange}
            handleImageChange={handleImageChange}
            addMultiValueRepeaterChange={addMultiValueRepeaterChange}
            removeMultiValueRepeaterChange={removeMultiValueRepeaterChange}
            updateMultiValueRepeaterChange={updateMultiValueRepeaterChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
            colorGrade={{
              bgColor,
              hoverBgColor,
              textColor,
              hexCode,
            }}
          />
        )}

        {linkId === "contact-us" && (
          <ContactUsCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            // Normal Field Change
            handleTextInputChange={handleTextInputChange}
            // Repeater Changes
            addMultiValueRepeaterChange={addMultiValueRepeaterChange}
            removeMultiValueRepeaterChange={removeMultiValueRepeaterChange}
            updateMultiValueRepeaterChange={updateMultiValueRepeaterChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
            colorGrade={{
              bgColor,
              hoverBgColor,
              textColor,
              hexCode,
            }}
          />
        )}

        {linkId === "faqs" && (
          <FAQsCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "privacy-and-quality-policy" && (
          <PrivacyAndQualityPolicyCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleTextEditorInputChange={handleTextEditorInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "registration-process" && (
          <RegistrationProcessCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleTextEditorInputChange={handleTextEditorInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "aml-or-ctf-laws" && (
          <AmlCTFRulesRegulationsInputFilds
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleTextEditorInputChange={handleTextEditorInputChange}
            handleImageChange={handleImageChange}
            addMultiValueRepeaterChange={addMultiValueRepeaterChange}
            removeMultiValueRepeaterChange={removeMultiValueRepeaterChange}
            updateMultiValueRepeaterChange={updateMultiValueRepeaterChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
            colorGrade={{
              bgColor,
              hoverBgColor,
              textColor,
              hexCode,
            }}
          />
        )}

        {linkId === "career" && (
          <CareersCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "fujairah-partners" && (
          <FujairahPartnersCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleTextEditorInputChange={handleTextEditorInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "news-blogs" && (
          <NewsAndBlogsCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "offers" && (
          <OffersCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleTextEditorInputChange={handleTextEditorInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        {linkId === "courses" && (
          <CoursesCMSForm
            activeTab={activeTab}
            isFetching={isFetching}
            formData={formData}
            selectedTab={selectedTab}
            allFilesResponse={allFilesResponse}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            handleTextInputChange={handleTextInputChange}
            handleImageChange={handleImageChange}
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}

        <div className="mt-10">
          {isFetching ? (
            <Skeleton className="w-[180px] h-[40px] rounded-md" />
          ) : (
            <button
              type="submit"
              disabled={isSubmitting}
              className={`min-w-[180px] ${bgColor} ${hoverBgColor} ${textColor} dark:text-light-weight-800 hover:text-white transition-300 px-5 py-2 rounded-md text-[14px] font-poppins-rg group flex items-center justify-center gap-2 ${isSubmitting ? "cursor-not-allowed" : ""}`}
            >
              {isSubmitting ? (
                <>
                  <ClipLoader color={hexCode} size={13} />
                  <span>Processing...</span>
                </>
              ) : (
                <span>Update</span>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default MainSectionForm;
